import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Table, Button, Card, Tag, Modal, Form, Select, Space, Tooltip, message } from "antd";
import { EyeOutlined, CheckOutlined, CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { getReq, postReq } from "../api";
import useUserDetails from "../utils/useUserDetails";
import Swal from "sweetalert2";
import { toastNotification } from "../utils/toasthelper";

const { Option } = Select;

const OnboardingCandidates = () => {
  const history = useHistory();
  const { userDetails } = useUserDetails();
  const [applications, setApplications] = useState([]);
  const [roles, setRoles] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Selection Modal state
  const [selectVisible, setSelectVisible] = useState(false);
  const [currentApp, setCurrentApp] = useState(null);

  const [form] = Form.useForm();
  const companyId = userDetails?.companyId || userDetails?._id;

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await postReq("/user/onboarding/list");
      if (res && res.status === 1) {
        setApplications(res.data || []);
      } else {
        message.error(res.message || "Failed to load onboarding applications");
      }
    } catch (err) {
      console.error(err);
      message.error("Error fetching onboarding list");
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      // Fetch Roles
      const rolesRes = await getReq("/user/roles");
      if (rolesRes && rolesRes.status === 1) {
        setRoles(rolesRes.data || []);
      } else if (Array.isArray(rolesRes)) {
        setRoles(rolesRes);
      } else if (rolesRes?.data && Array.isArray(rolesRes.data)) {
        setRoles(rolesRes.data);
      }

      // Fetch Users for Reporting Manager Selection
      const usersRes = await getReq("/user/dropdown_user");
      if (usersRes && usersRes.status === 200) {
        setUsersList(usersRes.data || []);
      }
    } catch (err) {
      console.error("Error fetching metadata:", err);
    }
  };

  useEffect(() => {
    fetchApplications();
    fetchMetadata();
  }, []);

  const copyOnboardingLink = () => {
    if (!companyId) {
      message.error("Unable to resolve Company ID. Try logging in again.");
      return;
    }
    const link = `${window.location.origin}/public/onboard?companyId=${companyId}`;
    navigator.clipboard.writeText(link);
    message.success("Onboarding link copied to clipboard!");
  };

  const handleReject = async (record) => {
    try {
      const result = await Swal.fire({
        title: "Reject Candidate?",
        text: `Are you sure you want to reject ${record.name}'s onboarding request?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, reject",
      });

      if (result.isConfirmed) {
        const res = await postReq(`/user/onboarding/update-status/${record._id}`, {
          status: "REJECTED",
        });
        if (res && res.status === 1) {
          toastNotification("success", "Candidate request rejected");
          fetchApplications();
        } else {
          toastNotification("error", res.message || "Failed to update status");
        }
      }
    } catch (err) {
      console.error(err);
      toastNotification("error", "An error occurred");
    }
  };

  const handleSelectClick = (record) => {
    setCurrentApp(record);
    form.setFieldsValue({
      roleId: undefined,
      reportingManager: undefined,
    });
    setSelectVisible(true);
  };

  const handleSelectSubmit = async (values) => {
    try {
      setSubmitting(true);
      const res = await postReq(`/user/onboarding/select/${currentApp._id}`, {
        roleId: values.roleId,
        reportingManager: values.reportingManager,
      });

      if (res && res.status === 1) {
        toastNotification("success", "Candidate selected and user created successfully!");
        setSelectVisible(false);
        fetchApplications();
      } else {
        toastNotification("error", res.message || "Failed to convert candidate to user");
      }
    } catch (err) {
      console.error(err);
      toastNotification("error", "Error creating user account");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Contact No",
      dataIndex: "mobileNo",
      key: "mobileNo",
    },
    {
      title: "Qualification",
      dataIndex: "qualification",
      key: "qualification",
      render: (text) => text || "-",
    },
    {
      title: "Applied Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "orange";
        if (status === "SELECTED") color = "green";
        if (status === "REJECTED") color = "red";
        return (
          <Tag color={color} style={{ fontWeight: 600, padding: "2px 8px", borderRadius: 4 }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Application Details">
            <Button
              type="text"
              icon={<EyeOutlined style={{ color: "#1890ff", fontSize: 16 }} />}
              onClick={() => {
                history.push(`/company/onboarding/${record._id}`);
              }}
            />
          </Tooltip>

          {record.status === "PENDING" && (
            <>
              <Tooltip title="Select & Create User Account">
                <Button
                  type="text"
                  icon={<CheckOutlined style={{ color: "#52c41a", fontSize: 16 }} />}
                  onClick={() => handleSelectClick(record)}
                />
              </Tooltip>
              <Tooltip title="Reject Request">
                <Button
                  type="text"
                  icon={<CloseOutlined style={{ color: "#f5222d", fontSize: 16 }} />}
                  onClick={() => handleReject(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: "#13487D" }}>Onboarding Submissions</span>
            <Button type="primary" icon={<CopyOutlined />} onClick={copyOnboardingLink} style={{ backgroundColor: "#13487D", borderColor: "#13487D", borderRadius: 6 }}>
              Copy Onboarding Link
            </Button>
          </div>
        }
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)" }}
      >
        <Table
          dataSource={applications}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Select & Role Assignment Modal */}
      <Modal
        title={
          <span style={{ fontSize: 18, fontWeight: 700, color: "#52c41a" }}>
            Approve & Onboard User
          </span>
        }
        open={selectVisible}
        onCancel={() => setSelectVisible(false)}
        footer={null}
        width={500}
      >
        {currentApp && (
          <Form form={form} layout="vertical" onFinish={handleSelectSubmit} style={{ marginTop: 15 }}>
            <p style={{ color: "#666", marginBottom: 20 }}>
              Assign a role and tech skills for <strong>{currentApp.name}</strong> to finalize their onboarding and create their login account.
            </p>

            <Form.Item
              name="roleId"
              label="Assign Role"
              rules={[{ required: true, message: "Please assign a system role" }]}
            >
              <Select placeholder="Select role" size="large">
                {roles.map((role) => (
                  <Option key={role._id} value={role._id}>
                    {role.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="reportingManager"
              label="Select Reporting Manager"
            >
              <Select placeholder="Select reporting manager" size="large" allowClear>
                {usersList.map((usr) => (
                  <Option key={usr._id} value={usr._id}>
                    {usr.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>


            <Form.Item style={{ marginTop: 30, textAlign: "right" }}>
              <Space>
                <Button onClick={() => setSelectVisible(false)} style={{ borderRadius: 6 }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={submitting} style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", borderRadius: 6 }}>
                  Submit & Onboard User
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default OnboardingCandidates;
